import { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { parseDate, formatDueDate } from '../lib/dates';
import { cn } from '../lib/utils';

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

// 42-cell (6 weeks) Monday-first grid covering the given month.
function monthGrid(view) {
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const lead = (first.getDay() + 6) % 7; // Mon = 0 … Sun = 6
  const start = new Date(view.getFullYear(), view.getMonth(), 1 - lead);
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
}

// Terminal-styled date picker: an input-like trigger that expands an inline
// monospace calendar. `value` / `onChange` use 'YYYY-MM-DD' strings ('' = none).
export default function DatePicker({ id, value, onChange }) {
  const now = new Date();
  const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const selected = parseDate(value);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const base = selected || todayMid;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const ref = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    // Reveal the full calendar (incl. footer) inside the modal's scroll area.
    panelRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    // Capture phase + stopPropagation so Escape only closes the calendar, not the
    // surrounding modal (the Dialog also listens for Escape on document, in the
    // bubble phase). Closing the modal here would discard the user's unsaved edits.
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const openPicker = () => {
    const base = selected || todayMid;
    setView(new Date(base.getFullYear(), base.getMonth(), 1));
    setOpen((o) => !o);
  };
  const pick = (d) => { onChange(toISO(d)); setOpen(false); };
  const clear = () => { onChange(''); setOpen(false); };
  const goToday = () => { setView(new Date(todayMid.getFullYear(), todayMid.getMonth(), 1)); pick(todayMid); };
  const shiftMonth = (delta) => setView((v) => new Date(v.getFullYear(), v.getMonth() + delta, 1));

  const monthLabel = view.toLocaleDateString(undefined, { month: 'long' });

  return (
    <div ref={ref}>
      <button
        type="button"
        id={id}
        onClick={openPicker}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm',
          'ring-offset-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          open && 'ring-2 ring-ring ring-offset-2',
        )}
      >
        <span className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          {value ? (
            <span className="tabular-nums">
              {value}<span className="ml-2 text-muted-foreground">{formatDueDate(value)}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">no due date</span>
          )}
        </span>
        {value ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear date"
            onClick={(e) => { e.stopPropagation(); clear(); }}
            className="rounded p-0.5 text-muted-foreground transition hover:bg-accent hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        ) : null}
      </button>

      {open ? (
        <div ref={panelRef} className="mt-1.5 rounded-md border border-border bg-card p-2 font-mono shadow-sm">
          <div className="flex items-center justify-between px-1 pb-2">
            <button type="button" onClick={() => shiftMonth(-1)} className="rounded p-1 text-muted-foreground transition hover:bg-accent hover:text-foreground" aria-label="Previous month">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs font-medium tabular-nums">
              {view.getFullYear()}-{pad(view.getMonth() + 1)}
              <span className="ml-2 text-muted-foreground">{monthLabel}</span>
            </span>
            <button type="button" onClick={() => shiftMonth(1)} className="rounded p-1 text-muted-foreground transition hover:bg-accent hover:text-foreground" aria-label="Next month">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 text-center text-[10px] uppercase text-muted-foreground/70">
            {WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {monthGrid(view).map((d) => {
              const outside = d.getMonth() !== view.getMonth();
              const isSel = sameDay(d, selected);
              const isToday = sameDay(d, todayMid);
              return (
                <button
                  key={toISO(d)}
                  type="button"
                  onClick={() => pick(d)}
                  className={cn(
                    'h-7 rounded text-xs tabular-nums transition',
                    outside && 'text-muted-foreground/40',
                    !isSel && 'hover:bg-accent',
                    isSel && 'bg-primary font-semibold text-primary-foreground',
                    isToday && !isSel && 'ring-1 ring-inset ring-primary/60',
                  )}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-2 flex items-center justify-between border-t border-border/60 pt-2 text-xs">
            <button type="button" onClick={goToday} className="text-primary transition hover:underline">$ today</button>
            <button type="button" onClick={clear} className="text-muted-foreground transition hover:text-foreground">$ clear</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
